import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Location, useLocation } from 'react-router';
import { useSessionStorage } from 'react-use';
import type { TLink } from '../components/breadcrumbs/link-item';

export enum ListNavigatorType {
  Student = 'STUDENT',
  Contact = 'CONTACT',
  Staff = 'STAFF',
  YearGroup = 'YEAR_GROUP',
  ClassGroup = 'CLASS_GROUP',
  ProgrammeStages = 'PROGRAMME_STAGES',
  SubjectGroup = 'SUBJECT_GROUP',
  SupportGroup = 'SUPPORT_GROUP',
  CustomGroup = 'CUSTOM_GROUP',
  Assessment = 'ASSESSMENT',
  StudentFees = 'STUDENT_FEES',
  Rule = 'RULE',
}

export type ListNavigatorSelectOption = {
  id: number | string;
  [key: string]: unknown;
};

export type UseDefaultListResult<RawItem> = {
  data: RawItem[] | null | undefined;
  isFetching: boolean;
};

export type UseDefaultListHook<RawItem> = (
  enabled: boolean,
) => UseDefaultListResult<RawItem>;

type ListNavigatorDataSource<
  StoreOption extends ListNavigatorSelectOption,
  RawItem,
> =
  | {
      defaultListData?: Array<StoreOption>;
      useDefaultList?: never;
      mapDefaultListItem?: never;
    }
  | {
      useDefaultList: UseDefaultListHook<RawItem>;
      mapDefaultListItem: (raw: RawItem) => StoreOption;
      defaultListData?: never;
    };

export type ListNavigatorSettingsParams<
  StoreOption extends ListNavigatorSelectOption,
  RawItem = StoreOption,
> = {
  type: ListNavigatorType;
  itemId?: StoreOption['id'];
  getNavigationUrl?: (navState: {
    currentLocation: Location<unknown>;
    currentItem: StoreOption;
    newItem: StoreOption;
  }) => string | undefined;
} & ListNavigatorDataSource<StoreOption, RawItem>;

function defaultGetNavigationUrl<
  StoreOption extends ListNavigatorSelectOption,
>({
  currentLocation,
  currentItem,
  newItem,
}: {
  currentLocation: Location<unknown>;
  currentItem: StoreOption;
  newItem: StoreOption;
}) {
  const url = new URL(
    `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`,
  );

  if (currentItem && newItem) {
    url.pathname = url.pathname.replace(`${currentItem.id}`, `${newItem.id}`);
  }

  return `${url.pathname}${url.search}`;
}

export type StoredList<StoreOption extends ListNavigatorSelectOption> = {
  parentPath?: TLink;
  listData: Array<StoreOption>;
};

const listNavigatorPrefix = 'tyro:profile-list-navigation';

const EMPTY_LIST: Array<never> = [];

function noopUseDefaultList<RawItem>(): UseDefaultListResult<RawItem> {
  return { data: undefined, isFetching: false };
}

export function useListNavigatorSettings<
  StoreOption extends ListNavigatorSelectOption,
  RawItem = StoreOption,
>({
  type,
  itemId,
  getNavigationUrl = defaultGetNavigationUrl,
  defaultListData,
  useDefaultList,
  mapDefaultListItem,
}: ListNavigatorSettingsParams<StoreOption, RawItem>) {
  const location = useLocation();

  const storeKey = `${listNavigatorPrefix}:${type}`;

  const [hasOpened, setHasOpened] = useState(false);

  const [currentItemId, setCurrentItemId] = useState<
    StoreOption['id'] | undefined
  >(itemId);
  const [persistedList, setPersistedList] = useSessionStorage<
    StoredList<StoreOption>
  >(storeKey, { listData: EMPTY_LIST as Array<StoreOption> });

  const persistedListData = persistedList.listData;
  const hasPersistedList = persistedListData.length > 0;

  const lazyHook = useDefaultList ?? noopUseDefaultList<RawItem>;
  const lazySource = lazyHook(hasOpened && !hasPersistedList);

  const lazyData = useMemo<Array<StoreOption> | undefined>(() => {
    if (!lazySource.data) return undefined;
    if (!mapDefaultListItem) return undefined;
    return lazySource.data.map(mapDefaultListItem);
  }, [lazySource.data, mapDefaultListItem]);

  const enableDefaultList = useCallback(() => {
    setHasOpened(true);
  }, []);

  const activeList = useMemo(() => {
    if (hasPersistedList) return persistedListData;
    if (defaultListData && defaultListData.length > 0) return defaultListData;
    return lazyData ?? (EMPTY_LIST as Array<StoreOption>);
  }, [hasPersistedList, persistedListData, defaultListData, lazyData]);

  const listById = useMemo(
    () => new Map(activeList.map((option) => [option.id, option])),
    [activeList],
  );

  const getItemIndexByItemId = useCallback(
    (id?: StoreOption['id']) =>
      activeList.findIndex((option) => option.id === id),
    [activeList],
  );

  const storeList = useCallback(
    (routeName: string | undefined, data: Array<StoreOption> | undefined) => {
      const parentPath = location.pathname;

      setPersistedList({
        parentPath: {
          name: routeName || '',
          href: parentPath,
        },
        listData: data ?? [],
      });
    },
    [location.pathname],
  );

  const getItemUrl = useCallback(
    (newItem: StoreOption) => {
      const currentItem = listById.get(itemId ?? '');

      const newUrl = getNavigationUrl({
        currentLocation: location,
        currentItem: currentItem || newItem,
        newItem,
      });

      return newUrl;
    },
    [listById, itemId, location],
  );

  const getPreviousUrl = useCallback(() => {
    const currentItemIndex = getItemIndexByItemId(itemId);
    if (currentItemIndex <= 0) return null;

    return getItemUrl(activeList[currentItemIndex - 1]);
  }, [itemId, activeList, getItemIndexByItemId, getItemUrl]);

  const getNextUrl = useCallback(() => {
    const currentItemIndex = getItemIndexByItemId(itemId);
    if (currentItemIndex === -1 || currentItemIndex === activeList.length - 1) {
      return null;
    }

    return getItemUrl(activeList[currentItemIndex + 1]);
  }, [itemId, activeList, getItemIndexByItemId, getItemUrl]);

  const setPreviousItemId = useCallback(() => {
    const currentItemIndex = getItemIndexByItemId(itemId);
    if (currentItemIndex <= 0) return;

    setCurrentItemId(activeList[currentItemIndex - 1].id);
  }, [itemId, activeList, getItemIndexByItemId]);

  const setNextItemId = useCallback(() => {
    const currentItemIndex = getItemIndexByItemId(itemId);
    if (currentItemIndex === -1 || currentItemIndex === activeList.length - 1) {
      return;
    }

    setCurrentItemId(activeList[currentItemIndex + 1].id);
  }, [itemId, activeList, getItemIndexByItemId]);

  useEffect(() => {
    if (
      itemId &&
      hasPersistedList &&
      !persistedListData.some((option) => option.id === itemId)
    ) {
      setPersistedList({ listData: [] });
    }

    setCurrentItemId(itemId);
  }, [itemId]);

  const storedList = useMemo<StoredList<StoreOption>>(
    () => ({
      parentPath: persistedList.parentPath,
      listData: activeList,
    }),
    [persistedList.parentPath, activeList],
  );

  const hasList = activeList.length > 0;

  return {
    isLoading: itemId !== currentItemId,
    isDefaultListLoading: lazySource.isFetching && !hasList,
    hasList,
    currentItem: listById.get(currentItemId ?? ''),
    setCurrentItemId,
    storedList,
    storeList,
    enableDefaultList,
    getPreviousUrl,
    getNextUrl,
    getItemUrl,
    setPreviousItemId,
    setNextItemId,
  };
}

export function clearAllListNavigatorSettings() {
  const keys = Object.keys(sessionStorage);
  for (const key of keys) {
    if (key.startsWith(listNavigatorPrefix)) {
      sessionStorage.removeItem(key);
    }
  }
}
